/*
  File: UserNewModal.tsx
  Author: Binish Mookken
  Date: April-June 2024

  Description:

*/

import { useEffect, useRef, useState } from "react";
import { useRolesData } from "./context/RolesContext";
import { Modal, Button, Input, Table, Checkbox, Popover, Alert } from "antd";
import Header from "./Header";
import { UserFormData, Role, Agent } from "./types";
import type { InputRef } from "antd";
import InputMask from "react-input-mask";

interface NewUserFormProps {
  formData: UserFormData;
  existingEmails: string[];
  existingPhones: string[];
  onFormDataChange: (data: Partial<UserFormData>) => void;
}

const formatPhoneNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, "").slice(0, 10);
  return cleaned;
};

const NewUserForm: React.FC<NewUserFormProps> = ({
  formData,
  existingEmails,
  existingPhones,
  onFormDataChange,
}) => {
  const { firstName, lastName, email, phone } = formData;

  const [errors, setErrors] = useState<Partial<UserFormData>>({});

  const firstNameRef = useRef<InputRef>(null);
  const lastNameRef = useRef<InputRef>(null);
  const emailRef = useRef<InputRef>(null);
  const phoneRef = useRef<InputRef>(null);

  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  const isEmailValid = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const isPhoneValid = (phone: string) => {
    phone = formatPhoneNumber(phone);
    const regex = /^\d{10}$/;
    return regex.test(phone);
  };

  const handleInputChange = (
    key: keyof UserFormData,
    value: string | boolean
  ) => {
    onFormDataChange({ ...formData, [key]: value });
    if (key === "email" && !isEmailValid(value as string)) {
      setErrors({ ...errors, [key]: "Invalid email format" });
    } else if (
      key === "email" &&
      existingEmails.some(
        (email) => email.toLowerCase() === (value as string).toLowerCase()
      )
    ) {
      setErrors({
        ...errors,
        [key]: "A user with the same email already exists",
      });
    } else if (key === "email") {
      setErrors({ ...errors, [key]: undefined });
    }

    if (key === "phone" && !isPhoneValid(value as string)) {
      setErrors({ ...errors, [key]: "Invalid phone number" });
    }
    // else if (existingPhones.includes(value as string)) {
    //   setErrors({
    //     ...errors,
    //     [key]: "A user with the same phone number already exists",
    //   });
    // }
    else if (key === "phone") {
      setErrors({ ...errors, [key]: undefined });
    }

    if (
      key === "firstName" &&
      (!value || typeof value !== "string" || value.length < 1)
    ) {
      setErrors({ ...errors, [key]: "First name is required" });
    } else if (key === "firstName") {
      setErrors({ ...errors, [key]: undefined });
    }

    if (
      key === "lastName" &&
      (!value || typeof value !== "string" || value.length < 1)
    ) {
      setErrors({ ...errors, [key]: "Last name is required" });
    } else if (key === "lastName") {
      setErrors({ ...errors, [key]: undefined });
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    nextRef: React.RefObject<InputRef>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef.current?.focus();
    }
  };

  return (
    <div style={{ margin: "0px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <div className={errors.firstName ? "field-error" : ""}>
          <label>First Name</label>
          <Input
            ref={firstNameRef}
            autoComplete="new-password"
            placeholder="First Name"
            value={firstName}
            onChange={(e) => handleInputChange("firstName", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, lastNameRef)}
            tabIndex={201}
          />
        </div>
        <div className={errors.lastName ? "field-error" : ""}>
          <label>Last Name</label>
          <Input
            ref={lastNameRef}
            autoComplete="new-password"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => handleInputChange("lastName", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, emailRef)}
            tabIndex={202}
          />
        </div>
        <div className={errors.email ? "field-error" : ""}>
          <label>Email</label>
          <Input
            ref={emailRef}
            autoComplete="new-password"
            placeholder="Email"
            value={email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, phoneRef)}
            tabIndex={203}
          />
          <div style={{ color: "#af6465", marginTop: "5px" }}>
            {errors["email"]}
          </div>
        </div>
        <div className={errors.phone ? "field-error" : ""}>
          <label>Phone</label>
          <InputMask
            mask="(999) 999-9999"
            value={phone}
            onChange={(e) =>
              handleInputChange("phone", formatPhoneNumber(e.target.value))
            }
            tabIndex={204}
            className="ant-input css-dev-only-do-not-override-1mmediq ant-input-outlined"
          ></InputMask>
          <div style={{ color: "#af6465", marginTop: "5px" }}>
            {errors["phone"]}
          </div>
        </div>
      </div>
    </div>
  );
};

interface UserNewModalProps {
  visible: boolean;
  existingEmails: string[];
  existingPhones: string[];
  onClose: () => void;
  onAddUser: (newUser: any) => void;
}

const UserNewModal: React.FC<UserNewModalProps> = ({
  visible,
  existingEmails,
  existingPhones,
  onClose,
  onAddUser,
}) => {
  const { rolesData } = useRolesData();
  const [formData, setFormData] = useState<any>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    roles: [],
  });

  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const handleFormDataChange = (updatedData: any) => {
    setFormData({ ...formData, ...updatedData });
  };

  const handleCreateUser = () => {
    onAddUser(formData);
  };

  const handleRowClick = (record: Role) => {
    const roleId = record.id;
    const updatedRoles = formData.roles.includes(roleId)
      ? formData.roles.filter((roleId: string) => roleId !== roleId)
      : [...formData.roles, roleId];
    setFormData({ ...formData, roles: updatedRoles });
  };

  const handleRoleChange = (selectedRole: Role) => {
    const updatedRoles = formData.roles.includes(selectedRole.id)
      ? formData.roles.filter((roleId: string) => roleId !== selectedRole.id)
      : [...formData.roles, selectedRole.id];
    setFormData({ ...formData, roles: updatedRoles });
  };

  const content = (role: Role) => {
    return (
      <div>
        <h3>{role.name}</h3>
        {role.role_permissions.length === 0 ? (
          <p>No permissions available</p>
        ) : (
          <ul>
            {role.role_permissions.map((permission) => (
              <li key={permission.id}>
                <strong>{permission.description}:</strong> {permission.notes}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  const columns = [
    {
      title: "Role Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: Role) => (
        <Popover
          style={{ background: "white" }}
          content={() => content(record)}
          title=""
          placement="left"
        >
          <span>{record.name}</span>
        </Popover>
      ),
    },
    {
      title: "Select",
      key: "select",
      render: (role: Role) => (
        <Checkbox
          checked={formData.roles.includes(role.id)}
          onChange={() => handleRoleChange(role)}
        />
      ),
    },
  ];

  useEffect(() => {
    const { firstName, lastName, email, phone, roles } = formData;
    const isValid =
      firstName &&
      lastName &&
      email &&
      phone &&
      roles.length > 0 &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
      /^\d{10}$/.test(formatPhoneNumber(phone));

    setIsFormValid(isValid);
  }, [formData]);

  useEffect(() => {
    if (visible) {
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        roles: [],
      });
      setIsFormValid(false);
    }
  }, [visible]);

  return (
    <Modal
      title={
        <Header
          header="User Details"
          subheader="Enter info for the new user below. Email address must be unique."
        />
      }
      open={visible}
      footer={null} // Remove the default footer
      width={800}
      closeIcon={null} // Add this line to remove the close icon
      className="no-padding-modal"
      onCancel={onClose}
    >
      <div style={{ padding: "10px" }}>
        <h3>Step 1: User Details</h3>
        <p>Enter info for the new user below. Email address must be unique</p>
        <NewUserForm
          formData={formData}
          onFormDataChange={handleFormDataChange}
          existingEmails={existingEmails}
          existingPhones={existingPhones}
        />
        <h3>Step 2: Select Security roles</h3>
        <Alert
          message="Please select at least one role. Hover on the role name to see the permissions associated with the role"
          type="info"
          showIcon
          closable
        />
        <Table
          rowKey="id"
          size="middle"
          columns={columns}
          dataSource={rolesData}
          pagination={false}
          rowClassName={(record) =>
            formData.roles && formData.roles.includes(record.id)
              ? "selected-row"
              : ""
          }
          onRow={(record, index) => ({
            tabIndex: 5,
            onClick: () => handleRowClick(record),
          })}
        />
        <div style={{ padding: "20px", borderTop: "1px solid #f0f0f0" }}>
          <Button
            key="cancel"
            onClick={onClose}
            style={{ marginRight: "10px" }}
          >
            Cancel
          </Button>
          <Button
            key="create"
            type="primary"
            onClick={handleCreateUser}
            disabled={!isFormValid}
          >
            Create
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UserNewModal;
